import { isEventParticipantStatus } from '../../../../utils/eventParticipation';
import { shouldHideArtistEventMembershipLabel } from './memberModalSharedDataUtils';
import { compareEventMemberRows } from './memberModalRowSorting';
import { resolveAllPerformancesPartBucket } from './eventParticipantUtils';
import {
  normalizeEventUuidList,
  normalizePartLabel,
  normalizeProfileEventPartAssignments,
} from './memberEventPartUtils';

const pickParticipantStatus = (artist) => {
  const fields = [
    artist?.contract_status,
    artist?.contractStatus,
    artist?.contract_artist_status,
    artist?.contractArtistStatus,
  ];
  for (const value of fields) {
    const text = typeof value === 'string' ? value.trim() : '';
    if (text) return text;
  }
  return '';
};

const pickResponseStatus = (artist) => {
  const fields = [
    artist?.response_status,
    artist?.responseStatus,
    artist?.contract_response_status,
    artist?.contractResponseStatus,
    artist?.participation_status,
    artist?.participationStatus,
  ];
  for (const value of fields) {
    const text = typeof value === 'string' ? value.trim().toLowerCase() : '';
    if (text) return text;
  }
  return '';
};

const isVisibleEventParticipantArtist = (artist) => {
  const contractStatus = pickParticipantStatus(artist);
  if (contractStatus) {
    return isEventParticipantStatus(contractStatus);
  }
  const responseStatus = pickResponseStatus(artist);
  if (!responseStatus) return true;
  return ![
    'declined',
    'rejected',
    'cancelled',
    'canceled',
    'not_agreed',
    'not-agreed',
    'disagreed',
    'ng',
    '辞退',
    '不参加',
    '未同意',
  ].includes(responseStatus);
};

const mergeEventParticipantArtistSources = ({
  snapshotArtist = null,
  selectedEventArtist = null,
}) => {
  if (snapshotArtist && !selectedEventArtist) return snapshotArtist;
  if (!snapshotArtist && selectedEventArtist) return selectedEventArtist;
  if (!snapshotArtist && !selectedEventArtist) return null;

  const merged = {
    ...snapshotArtist,
    ...selectedEventArtist,
  };

  const mergedEventUuids = normalizeEventUuidList([
    ...(Array.isArray(snapshotArtist?.event_uuids) ? snapshotArtist.event_uuids : []),
    ...(Array.isArray(selectedEventArtist?.event_uuids) ? selectedEventArtist.event_uuids : []),
  ]);
  if (mergedEventUuids.length > 0) {
    merged.event_uuids = mergedEventUuids;
  }

  const assignmentsByEventUuid = new Map();
  [
    ...normalizeProfileEventPartAssignments(snapshotArtist?.event_part_assignments),
    ...normalizeProfileEventPartAssignments(snapshotArtist?.eventPartAssignments),
    ...normalizeProfileEventPartAssignments(selectedEventArtist?.event_part_assignments),
    ...normalizeProfileEventPartAssignments(selectedEventArtist?.eventPartAssignments),
  ].forEach((assignment) => {
    if (!assignment?.eventUuid) return;
    assignmentsByEventUuid.set(assignment.eventUuid, {
      event_uuid: assignment.eventUuid,
      part_label: assignment.partLabel,
      has_assignment: assignment.hasAssignment,
      part_sort_order: assignment.partSortOrder,
      casting_info_id: assignment.castingInfoId,
    });
  });
  if (assignmentsByEventUuid.size > 0) {
    merged.event_part_assignments = Array.from(assignmentsByEventUuid.values());
  }

  if (Array.isArray(snapshotArtist?.group_events) && snapshotArtist.group_events.length > 0) {
    merged.group_events = snapshotArtist.group_events;
  } else if (Array.isArray(snapshotArtist?.groupEvents) && snapshotArtist.groupEvents.length > 0) {
    merged.group_events = snapshotArtist.groupEvents;
  }

  return merged;
};

export function buildEventAdminParticipants({
  participantSnapshot,
  selectedEventAdmins = [],
  selectedEventId = '',
  pickFirstString,
  normalizeEventUuidList,
  buildParticipantEventMembershipLabel,
  buildAdminProfilePayload,
  buildAvatarUrl,
}) {
  const source =
    Array.isArray(participantSnapshot?.admins) && participantSnapshot.admins.length > 0
      ? participantSnapshot.admins
      : selectedEventAdmins;
  const seen = new Set();
  return source
    .map((admin, idx) => {
      const keySource = admin?.admin_user_id ?? admin?.id ?? admin?.user_id ?? idx;
      const dedupeKey = String(keySource);
      if (seen.has(dedupeKey)) return null;
      seen.add(dedupeKey);

      const name = pickFirstString(
        admin?.display_name,
        admin?.full_name,
        admin?.admin_name,
        admin?.username,
        '担当者'
      );
      const avatarKey = admin?.profile_picture || admin?.admin_profile_picture;
      const eventUuids = normalizeEventUuidList(
        admin?.event_uuids || (selectedEventId ? [selectedEventId] : [])
      );
      const eventMembershipLabel = buildParticipantEventMembershipLabel(eventUuids);
      const payload = buildAdminProfilePayload({
        ...admin,
        eventMembershipLabel,
      });
      return {
        key: `admin-${dedupeKey}`,
        adminUserId: keySource,
        name,
        position: admin?.position || '',
        department: admin?.department || '',
        avatarUrl: buildAvatarUrl(avatarKey),
        initial: (name || '').trim().charAt(0) || '',
        payload,
        eventMembershipLabel,
      };
    })
    .filter(Boolean);
}

function mapConciergeParticipant({
  planner,
  index,
  fallbackEventUuids = [],
  pickFirstString,
  normalizeEventUuidList,
  buildParticipantEventMembershipLabel,
  buildArtistProfilePayload,
  buildAvatarUrl,
}) {
  const name = pickFirstString(
    planner?.artist_name,
    planner?.name,
    planner?.display_name,
    planner?.full_name
  ) || '企画者';
  const key = planner?.artist_id ?? planner?.id ?? index;
  const avatarKey = planner?.artist_profile_picture || planner?.profile_picture;
  const eventUuids = normalizeEventUuidList(planner?.event_uuids || fallbackEventUuids);
  const eventMembershipLabel = buildParticipantEventMembershipLabel(eventUuids);
  const payload = buildArtistProfilePayload({
    ...planner,
    artist_name: name,
    name,
    short: '企画者',
    mainPart: '企画者',
    partLabel: '企画者',
    eventMembershipLabel,
  });
  return {
    key: `concierge-${key}`,
    name,
    badge: '',
    status: '',
    avatarUrl: buildAvatarUrl(avatarKey),
    initial: (name || '').trim().charAt(0) || '',
    payload,
    eventMembershipLabel,
  };
}

export function buildEventConciergeParticipants({
  participantSnapshot,
  conciergePlanner,
  isConciergeEvent,
  selectedEventId = '',
  pickFirstString,
  normalizeEventUuidList,
  buildParticipantEventMembershipLabel,
  buildArtistProfilePayload,
  buildAvatarUrl,
}) {
  const fromSnapshot = Array.isArray(participantSnapshot?.concierge_planners)
    ? participantSnapshot.concierge_planners
    : [];
  if (fromSnapshot.length > 0) {
    return fromSnapshot.map((planner, idx) =>
      mapConciergeParticipant({
        planner,
        index: idx,
        pickFirstString,
        normalizeEventUuidList,
        buildParticipantEventMembershipLabel,
        buildArtistProfilePayload,
        buildAvatarUrl,
      })
    );
  }

  if (!isConciergeEvent || !conciergePlanner) return [];
  return [
    mapConciergeParticipant({
      planner: conciergePlanner,
      index: conciergePlanner?.artist_id ?? conciergePlanner?.id ?? 'self',
      fallbackEventUuids: selectedEventId ? [selectedEventId] : [],
      pickFirstString,
      normalizeEventUuidList,
      buildParticipantEventMembershipLabel,
      buildArtistProfilePayload,
      buildAvatarUrl,
    }),
  ];
}

export function buildEventParticipantScopeTabs({
  effectiveGroupEvents = [],
  pickFirstString,
}) {
  if (effectiveGroupEvents.length <= 1) return [];
  return effectiveGroupEvents.map((event) => ({
    eventUuid: String(event?.event_uuid || ''),
    label: pickFirstString(event?.performance_type, event?.name) || '公演未設定',
  }));
}

export function resolveEventScopedPartMeta({
  mergedArtist,
  participantPartScopeEventUuid,
  isAllPerformancesScope,
  fallbackPartLabel = '',
  pickFirstString,
  normalizePartLabel,
  castingInfoMap,
}) {
  if (
    !participantPartScopeEventUuid ||
    participantPartScopeEventUuid === 'all' ||
    isAllPerformancesScope ||
    (
      !Array.isArray(mergedArtist?.event_part_assignments)
      && !Array.isArray(mergedArtist?.eventPartAssignments)
    )
  ) {
    return { displayPartLabel: '', scopedPartOrder: null };
  }
  const scoped = normalizeProfileEventPartAssignments(
    mergedArtist?.event_part_assignments || mergedArtist?.eventPartAssignments
  ).find((assignment) => assignment.eventUuid === String(participantPartScopeEventUuid));
  const scopedLabel = normalizePartLabel(
    pickFirstString(scoped?.partLabel, scoped?.part, scoped?.label)
  );
  const resolvedFallbackPartLabel = normalizePartLabel(fallbackPartLabel) || '未設定';
  const hasScopedAssignment =
    scoped?.hasAssignment === true ||
    scoped?.castingInfoId != null ||
    Boolean(scopedLabel);
  const displayPartLabel = hasScopedAssignment
    ? (scopedLabel || resolvedFallbackPartLabel)
    : resolvedFallbackPartLabel;
  const scopedCasting = scoped?.castingInfoId != null
    ? castingInfoMap.get(String(scoped.castingInfoId))
    : null;
  let scopedPartOrder = null;
  if (Number.isFinite(scoped?.partSortOrder)) {
    scopedPartOrder = scoped.partSortOrder;
  } else if (Number.isFinite(scopedCasting?.sort_order)) {
    scopedPartOrder = scopedCasting.sort_order;
  }
  return { displayPartLabel, scopedPartOrder };
}

export function groupEventParticipantsByPart({
  artistParticipants = [],
  participantPartScopeEventUuid = 'all',
  getPartOrder,
}) {
  if (!artistParticipants.length) return [];
  const isAllPerformancesScope = String(participantPartScopeEventUuid) === 'all';
  if (isAllPerformancesScope) {
    const groups = new Map();
    const fallbackOrder = getPartOrder(null);
    artistParticipants.forEach((person) => {
      const label = person?.allPerformancesGroupLabel || person?.mainPartLabel || 'パート未設定';
      if (!groups.has(label)) {
        groups.set(label, {
          label,
          order: Number.isFinite(person?.allPerformancesGroupOrder)
            ? person.allPerformancesGroupOrder
            : fallbackOrder,
          members: [],
        });
      }
      groups.get(label).members.push(person);
    });
    return Array.from(groups.values()).sort((a, b) => {
      const ao = Number.isFinite(a.order) ? a.order : fallbackOrder;
      const bo = Number.isFinite(b.order) ? b.order : fallbackOrder;
      if (ao !== bo) return ao - bo;
      return a.label.localeCompare(b.label, 'ja');
    });
  }
  const groups = new Map();
  const fallbackOrder = getPartOrder(null);
  artistParticipants.forEach((person) => {
    const label = person.partLabel || person.badge || 'パート未設定';
    if (!groups.has(label)) {
      groups.set(label, {
        label,
        order: Number.isFinite(person.order) ? person.order : fallbackOrder,
        members: [],
      });
    }
    groups.get(label).members.push(person);
  });
  return Array.from(groups.values()).sort((a, b) => {
    const ao = Number.isFinite(a.order) ? a.order : fallbackOrder;
    const bo = Number.isFinite(b.order) ? b.order : fallbackOrder;
    if (ao !== bo) return ao - bo;
    return a.label.localeCompare(b.label, 'ja');
  });
}

export function buildEventArtistParticipants({
  participantSnapshot,
  participantArtists = [],
  participantArtistMap,
  pickFirstString,
  normalizeEventUuidList,
  participantPartScopeEventUuid = 'all',
  selectedEventId = '',
  effectiveGroupEvents = [],
  getPartOrder,
  buildParticipantEventMembershipLabel,
  buildParticipantEventPartSummary,
  buildArtistProfilePayload,
  buildAvatarUrl,
  resolveArtistParts,
  resolveFallbackPartLabel,
  resolveScopedPartMeta,
  isUnifiedEventGroupChat = false,
}) {
  const normalizedFallbackArtists = (Array.isArray(participantArtists) ? participantArtists : []).filter(
    (artist) => isEventParticipantStatus(artist?.contract_status)
  );
  const snapshotArtists = Array.isArray(participantSnapshot?.artists)
    ? participantSnapshot.artists
    : [];
  const manualSnapshotArtists = Array.isArray(participantSnapshot?.manual_chat_artists)
    ? participantSnapshot.manual_chat_artists
    : [];
  const manualArtistIds = new Set(
    manualSnapshotArtists
      .map((artist) => String(artist?.artist_id ?? artist?.id ?? '').trim())
      .filter(Boolean)
  );
  const hasParticipantSnapshot = Array.isArray(participantSnapshot?.artists) || Array.isArray(participantSnapshot?.manual_chat_artists);
  const sourceArtists = (hasParticipantSnapshot
    ? [
        ...snapshotArtists
          .filter((artist) => !manualArtistIds.has(String(artist?.artist_id ?? artist?.id ?? '').trim()))
          .map((artist) => ({ artist, memberListSectionOrder: 0 })),
        ...manualSnapshotArtists.map((artist) => ({ artist, memberListSectionOrder: 1 })),
      ]
    : normalizedFallbackArtists.map((artist) => ({ artist, memberListSectionOrder: 0 })))
    .filter(({ artist }) => isVisibleEventParticipantArtist(artist));
  const seen = new Set();
  const artists = sourceArtists
    .map(({ artist, memberListSectionOrder }, idx) => {
      const rawArtistId = artist?.artist_id ?? artist?.id ?? null;
      const dedupeKey = rawArtistId == null ? `idx-${idx}` : String(rawArtistId);
      if (seen.has(dedupeKey)) return null;
      seen.add(dedupeKey);

      const selectedEventArtist =
        rawArtistId == null ? null : participantArtistMap.get(String(rawArtistId));
      const mergedArtist = mergeEventParticipantArtistSources({
        snapshotArtist: artist,
        selectedEventArtist,
      }) || artist;
      const name = pickFirstString(mergedArtist?.artist_name, mergedArtist?.name) || 'アーティスト';
      const avatarKey = mergedArtist?.profile_picture || mergedArtist?.artist_profile_picture;
      const gender = pickFirstString(
        mergedArtist?.gender,
        mergedArtist?.artist_gender,
        mergedArtist?.artistGender
      );
      const artistParts = typeof resolveArtistParts === 'function'
        ? resolveArtistParts(mergedArtist)
        : (Array.isArray(mergedArtist?.artist_parts) ? mergedArtist.artist_parts : (mergedArtist?.parts || []));
      const mainPartId = Array.isArray(artistParts) && artistParts.length ? artistParts[0] : null;
      const fallbackPartLabel =
        typeof resolveFallbackPartLabel === 'function'
          ? resolveFallbackPartLabel(mergedArtist, mainPartId)
          : 'パート未設定';
      const partSummary = buildParticipantEventPartSummary(mergedArtist, fallbackPartLabel);
      const isAllPerformancesScope = String(participantPartScopeEventUuid) === 'all';
      const profilePartOrder = getPartOrder(mainPartId);
      const allPerformancesPartBucket = isAllPerformancesScope
        ? resolveAllPerformancesPartBucket({
            partSummary,
            fallbackPartLabel,
            fallbackOrder: profilePartOrder,
            mixedOrder: getPartOrder(null) + 1000,
            resolveCommonPartOrder: ({ partLabel }) => {
              const assignments = normalizeProfileEventPartAssignments(
                mergedArtist?.event_part_assignments || mergedArtist?.eventPartAssignments
              );
              const matched = assignments.find((assignment) => {
                const assignmentPartLabel = normalizePartLabel(assignment?.partLabel);
                return assignmentPartLabel && assignmentPartLabel === partLabel;
              });
              return Number.isFinite(matched?.partSortOrder)
                ? matched.partSortOrder
                : null;
            },
          })
        : null;
      const scopedPartMeta =
        typeof resolveScopedPartMeta === 'function'
          ? resolveScopedPartMeta({
              mergedArtist,
              participantPartScopeEventUuid,
              mainPartId,
              isAllPerformancesScope,
              fallbackPartLabel,
            })
          : { displayPartLabel: '', scopedPartOrder: null };
      const displayPartLabel = scopedPartMeta?.displayPartLabel || '';
      const scopedPartOrder = scopedPartMeta?.scopedPartOrder ?? null;
      const partLabel = partSummary.partLabel || 'パート未設定';
      const sortOrder = isAllPerformancesScope
        ? profilePartOrder
        : (Number.isFinite(scopedPartOrder) ? scopedPartOrder : profilePartOrder);
      const status =
        mergedArtist?.progress_label ||
        mergedArtist?.status_label ||
        mergedArtist?.status ||
        '';
      const eventUuids = normalizeEventUuidList(
        mergedArtist?.event_uuids || (selectedEventId ? [selectedEventId] : [])
      );
      const eventMembershipLabel = shouldHideArtistEventMembershipLabel(mergedArtist)
        ? ''
        : buildParticipantEventMembershipLabel(eventUuids);
      const payload = buildArtistProfilePayload({
        ...mergedArtist,
        partLabel,
        eventPartLabel: partSummary.eventPartLabel,
        eventMembershipLabel,
        group_events: effectiveGroupEvents,
        isUnifiedEventGroupChat,
      });
      const customRoles = Array.isArray(mergedArtist?.custom_roles)
        ? mergedArtist.custom_roles
        : Array.isArray(mergedArtist?.customRoles)
          ? mergedArtist.customRoles
          : [];
      const contractTs = mergedArtist?.contractTs ?? (() => {
        const parsed = Date.parse(mergedArtist?.contract_updated_at || '');
        return Number.isFinite(parsed) ? parsed : null;
      })();
      return {
        key: `artist-${dedupeKey}`,
        name,
        badge: isAllPerformancesScope ? '' : displayPartLabel,
        partLabel: isAllPerformancesScope ? '' : displayPartLabel,
        status,
        gender,
        pic: avatarKey,
        avatarUrl: buildAvatarUrl(avatarKey),
        initial: (name || '').trim().charAt(0) || '',
        order: sortOrder,
        sortOrder,
        contractTs,
        memberListSectionOrder,
        memberListSourceIndex: idx,
        payload,
        isChief: !!mergedArtist?.is_chief,
        isConcertmaster: !!mergedArtist?.is_concertmaster,
        customRoles,
        eventPartLabel: isAllPerformancesScope
          ? (allPerformancesPartBucket?.eventPartLabel || '')
          : '',
        eventMembershipLabel,
        allPerformancesGroupLabel: allPerformancesPartBucket?.sectionLabel || '',
        allPerformancesGroupOrder: allPerformancesPartBucket?.sectionOrder,
        selectionPartLabel:
          (isAllPerformancesScope ? allPerformancesPartBucket?.sectionLabel : displayPartLabel)
          || fallbackPartLabel
          || 'パート未設定',
      };
    })
    .filter(Boolean);
  artists.sort((a, b) => {
    if (a.memberListSectionOrder !== b.memberListSectionOrder) {
      return a.memberListSectionOrder - b.memberListSectionOrder;
    }
    if (a.memberListSectionOrder === 1) {
      return a.memberListSourceIndex - b.memberListSourceIndex;
    }
    return compareEventMemberRows(a, b);
  });
  return artists;
}

export function buildEventDerivedGroupArtistsFromMemberList({
  decidedSections = [],
  undecidedList = [],
  manualChatArtists = [],
  enrichedArtists = [],
  pickFirstString = (...values) => values.find((value) => String(value || '').trim()) || '',
}) {
  const ordered = (Array.isArray(decidedSections) ? decidedSections : []).flatMap((section) =>
    (Array.isArray(section?.members) ? section.members : []).map((member) => ({
      ...member,
      selectionPartLabel: section?.title || member?.partLabel || member?.mainPartLabel || 'パート未設定',
    }))
  );
  ordered.push(...(Array.isArray(undecidedList) ? undecidedList : []).map((member) => ({
    ...member,
    selectionPartLabel: member?.partLabel || member?.mainPartLabel || 'パート未設定',
  })));

  const enrichedById = new Map(
    (Array.isArray(enrichedArtists) ? enrichedArtists : []).map((artist) => [
      String(
        artist?.payload?.artist_id
        || artist?.payload?.id
        || artist?.artist_id
        || artist?.id
        || ''
      ).trim(),
      artist,
    ])
  );
  (Array.isArray(manualChatArtists) ? manualChatArtists : []).forEach((artist) => {
    const artistId = String(artist?.artist_id || artist?.id || '').trim();
    if (!artistId) return;
    const enriched = enrichedById.get(artistId);
    ordered.push(enriched || {
      ...artist,
      name: pickFirstString(artist?.artist_name, artist?.name) || 'アーティスト',
      pic: artist?.artist_profile_picture || artist?.profile_picture || null,
      gender: artist?.artist_gender || artist?.gender || null,
      selectionPartLabel: artist?.artist_part_label || artist?.artist_part_short_name || 'パート未設定',
    });
  });
  return ordered;
}
